import { useMemo, useState } from 'react';
import { ChevronDown, Settings2 } from 'lucide-react';
import type { AntiLagData, CacheEnvelope } from '../../types/throttle';
import {
  describeThrottleTarget,
  formatEpochMsAgo,
  isMsptThrottleConfig,
  isPlayerCountThrottleConfig,
} from '../../utils/throttle';
import {
  MobAiRuleTable,
  MsptRuleTable,
  PlayerCountRuleTable,
  UnknownRulesNote,
} from './ThrottleRuleTables';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';

interface ThrottleConfigPanelProps {
  config: CacheEnvelope<AntiLagData> | null;
}

/** Collapsible panel rendering the network-wide anti-lag configuration document. */
export const ThrottleConfigPanel = ({ config }: ThrottleConfigPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const rules = useMemo(() => config?.data.chunkThrottleConfigs ?? [], [config]);
  // One list holds both rule families — split before rendering, they share no fields.
  const msptRules = useMemo(() => rules.filter(isMsptThrottleConfig), [rules]);
  const playerCountRules = useMemo(() => rules.filter(isPlayerCountThrottleConfig), [rules]);
  const mobAiRules = config?.data.mobAIThrottleConfigs ?? [];

  return (
    <div className="glass-card p-4 md:p-5">
      <div
        className="cursor-pointer select-none"
        role="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <SectionHeader
          className="mb-0"
          icon={Settings2}
          title="Anti-Lag Configuration"
          description={config ? `as of ${formatEpochMsAgo(config.fetchedAt)}` : undefined}
          actions={
            <ChevronDown className={cn('w-4 h-4 text-glass-400 transition-transform', isExpanded && 'rotate-180')} />
          }
        />
      </div>

      {isExpanded && (
        <div className="mt-4 border-t border-glass-200/10 pt-4 space-y-4">
          {!config ? (
            <p className="text-sm text-glass-400">
              Configuration has not been loaded by the data pipeline yet.
            </p>
          ) : (
            <>
              {/* Chunk throttle rules */}
              <div className="subcard p-3 md:p-4">
                <h4 className="text-sm font-semibold text-white mb-2">Chunk throttle rules</h4>
                {msptRules.length === 0 ? (
                  <p className="text-sm text-glass-400">
                    No rules configured — all servers run on built-in defaults.
                  </p>
                ) : (
                  <MsptRuleTable rules={msptRules} showTarget />
                )}
                <p className="mt-2 text-xs text-glass-500">
                  Rules sharing a target are escalating tiers: heavier chunks fall under harsher
                  tiers. Flagged chunks start in WARNING (owners get in-game chat warnings); if
                  they stay laggy past the grace period they become THROTTLED and tick at the
                  rule's throttle multiplier (×1 = warning only). The most specific matching
                  target level wins (single &gt; cluster &gt; all).
                </p>
                <UnknownRulesNote rules={rules} />
              </div>

              {/* Server-wide population throttles */}
              {playerCountRules.length > 0 && (
                <div className="subcard p-3 md:p-4">
                  <h4 className="text-sm font-semibold text-white mb-2">
                    Server-wide population throttles
                  </h4>
                  <PlayerCountRuleTable rules={playerCountRules} showTarget />
                  <p className="mt-2 text-xs text-glass-500">
                    These apply to the whole server rather than to individual chunks: once a
                    server holds the listed player count, everything runs at the multiplier. The
                    highest matching tier wins — a server's detail page shows which one is in
                    force.
                  </p>
                </div>
              )}

              {/* Mob AI throttles */}
              {mobAiRules.length > 0 && (
                <div className="subcard p-3 md:p-4">
                  <h4 className="text-sm font-semibold text-white mb-2">Mob AI throttles</h4>
                  <MobAiRuleTable rules={mobAiRules} showTarget />
                  <p className="mt-2 text-xs text-glass-500">
                    Population-based: past the listed player count, mobs further than the AI
                    radius from every player stop ticking their AI. The highest matching tier
                    wins.
                  </p>
                </div>
              )}

              {/* Block limits */}
              <div className="subcard p-3 md:p-4">
                <h4 className="text-sm font-semibold text-white mb-2">Block limits</h4>
                {config.data.blockLimits.length === 0 ? (
                  <p className="text-sm text-glass-400">No block limits configured.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead>
                        <tr>
                          <th className="th px-3">Applies to</th>
                          <th className="th px-3">Block</th>
                          <th className="th px-3" title="Maximum placed per player on the targeted servers">
                            Per-player limit
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {config.data.blockLimits.map((limit, index) => (
                          <tr key={`${limit.target}-${limit.blockId}-${index}`}>
                            <td className="td px-3 py-2 text-glass-300" title={limit.target}>
                              {describeThrottleTarget(limit.target)}
                            </td>
                            <td className="td px-3 py-2 text-white font-mono text-xs">{limit.blockId}</td>
                            <td className="td px-3 py-2 text-glass-300 tabular-nums">{limit.limit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
